import type { FC } from "react";
import { Outlet } from "react-router-dom";

import { Button, LanguageSelect } from "../shared";
import { useRegulatorMe } from "../../queries/regulator-portal/useRegulatorMe";
import { useRegulatorSignOut } from "../../queries/regulator-portal/useRegulatorSignOut";
import RegulatorPortalNav from "./RegulatorPortalNav";
import { clearRegulatorPortalAndRedirect } from "../../utils/tokenUtils";
import {
  getStoredGuestLanguage,
  setStoredGuestLanguage,
} from "../../utils/i18n/languagePreference";
import {
  DEFAULT_LANGUAGE,
  canonicalizeLanguageTag,
} from "../../utils/i18n/supportedLanguages";
import { BRAND_ICONS_URLS } from "../../utils/Constants";
import { AppStrings } from "../../utils/constants/AppStrings";

import styles from "./RegulatorPortalLayout.module.css";

const BRAND_LOGO_SRC = `${process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL ?? ""}${BRAND_ICONS_URLS.topLeftAppIcon}`;

/**
 * Shell for the regulator portal: brand row, identity, language, sign out. No
 * Sider and no PAM navigation — a regulator sees this and the pages below it,
 * nothing else. Identity comes from `/me`, the same response the guard routes
 * on.
 */
const RegulatorPortalLayout: FC = () => {
  const { data } = useRegulatorMe();
  const signOut = useRegulatorSignOut();

  // Regulators have no profile-preference PATCH and portal routes skip the
  // platform-config fetch, so the guest key is the whole persistence story
  // (same store-then-reload apply as the login screen; boot keying in
  // src/index.js re-applies it).
  const activeLanguage =
    canonicalizeLanguageTag(getStoredGuestLanguage()) ?? DEFAULT_LANGUAGE;

  const handleLanguageChange = (lng: string) => {
    if (!lng || lng === activeLanguage) return;
    setStoredGuestLanguage(lng);
    window.location.reload();
  };

  // Server sign-out is best-effort: the client session is torn down whether
  // or not Cognito revocation succeeded (the affiliate portal pattern).
  const handleSignOutClicked = () => {
    signOut.mutate(undefined, {
      onSettled: () => {
        clearRegulatorPortalAndRedirect();
      },
    });
  };

  return (
    <div className={styles.shell} data-testid="regulator-portal-layout">
      <header className={styles.header}>
        <div className={styles.brandRow}>
          <span className={styles.logo}>
            <img
              className={styles.logoImage}
              src={BRAND_LOGO_SRC}
              alt={AppStrings.REBET}
              width={22}
              height={22}
            />
          </span>
          <span className={styles.brand}>
            {AppStrings.PAM_REGULATOR_PORTAL}
          </span>
        </div>
        <div className={styles.identity}>
          <LanguageSelect
            className={styles.language}
            value={activeLanguage}
            onChange={handleLanguageChange}
            data-testid="regulator-portal-language"
          />
          <p className={styles.email} data-testid="regulator-portal-email">
            {data?.email || AppStrings.UNKNOWN_USER}
          </p>
          <Button
            intent="neutral"
            onClick={handleSignOutClicked}
            disabled={signOut.isPending}
            data-testid="regulator-portal-logout"
          >
            {AppStrings.SIGN_OUT}
          </Button>
        </div>
      </header>
      <RegulatorPortalNav />
      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
};

export default RegulatorPortalLayout;
